type MaybePromise<T> = Promise<T> | T;

// export function asyncPipe<A, B>(
//   ab: (a: A) => MaybePromise<B>
// ): (a: MaybePromise<A>) => Promise<B>;

// export function asyncPipe<A, B, C>(
//   ab: (a: A) => MaybePromise<B>,
//   bc: (b: B) => MaybePromise<C>
// ): (a: MaybePromise<A>) => Promise<C>;

/**
 * This big scary pipe interface allows for very nice type safety for piped functions
 *
 * It allows for the input type to differ from the output type
 * */
interface AsyncPipe {
  <A, B>(fn1: (input: A) => B): (value: MaybePromise<A>) => Promise<B>;
  <A, B, C>(fn1: (input: A) => MaybePromise<B>, fn2: (input: B) => C): (
    value: MaybePromise<A>
  ) => Promise<C>;
  <A, B, C, D>(
    fn1: (input: A) => MaybePromise<B>,
    fn2: (input: B) => MaybePromise<C>,
    fn3: (input: C) => MaybePromise<D>
  ): (value: MaybePromise<A>) => Promise<D>;
  <A, B, C, D, E>(
    fn1: (input: A) => MaybePromise<B>,
    fn2: (input: B) => MaybePromise<C>,
    fn3: (input: C) => MaybePromise<D>,
    fn4: (input: D) => MaybePromise<E>
  ): (value: MaybePromise<A>) => E;
  <A, B, C, D, E, F>(
    fn1: (input: A) => MaybePromise<B>,
    fn2: (input: B) => MaybePromise<C>,
    fn3: (input: C) => MaybePromise<D>,
    fn4: (input: D) => MaybePromise<E>,
    fn5: (input: E) => MaybePromise<F>
  ): (value: MaybePromise<A>) => Promise<F>;
  <A, B, C, D, E, F, G>(
    fn1: (input: A) => MaybePromise<B>,
    fn2: (input: B) => MaybePromise<C>,
    fn3: (input: C) => MaybePromise<D>,
    fn4: (input: D) => MaybePromise<E>,
    fn5: (input: E) => MaybePromise<F>,
    fn6: (input: F) => MaybePromise<G>
  ): (value: MaybePromise<A>) => Promise<G>;
  <A, B, C, D, E, F, G, H>(
    fn1: (input: A) => MaybePromise<B>,
    fn2: (input: B) => MaybePromise<C>,
    fn3: (input: C) => MaybePromise<D>,
    fn4: (input: D) => MaybePromise<E>,
    fn5: (input: E) => MaybePromise<F>,
    fn6: (input: F) => MaybePromise<G>,
    fn7: (input: G) => MaybePromise<H>
  ): (value: MaybePromise<A>) => Promise<H>;
  <A, B, C, D, E, F, G, H, I>(
    fn1: (input: A) => MaybePromise<B>,
    fn2: (input: B) => MaybePromise<C>,
    fn3: (input: C) => MaybePromise<D>,
    fn4: (input: D) => MaybePromise<E>,
    fn5: (input: E) => MaybePromise<F>,
    fn6: (input: F) => MaybePromise<G>,
    fn7: (input: G) => MaybePromise<H>,
    fn8: (input: H) => MaybePromise<I>
  ): (value: MaybePromise<A>) => Promise<I>;
  <A, B, C, D, E, F, G, H, I, J>(
    fn1: (input: A) => MaybePromise<B>,
    fn2: (input: B) => MaybePromise<C>,
    fn3: (input: C) => MaybePromise<D>,
    fn4: (input: D) => MaybePromise<E>,
    fn5: (input: E) => MaybePromise<F>,
    fn6: (input: F) => MaybePromise<G>,
    fn7: (input: G) => MaybePromise<H>,
    fn8: (input: H) => MaybePromise<I>,
    fn9: (input: I) => MaybePromise<J>
  ): (value: MaybePromise<A>) => Promise<J>;
}

// eslint-disable-next-line @typescript-eslint/ban-types
export const asyncPipe: AsyncPipe =
  (...fns: CallableFunction[]) =>
  (x: any): Promise<unknown> => {
    return fns.reduce(async (y, fn) => fn(await y), x);
  };
